import { ChevronDownIcon, ChevronUpIcon } from '@/components/base/Icons/RocheIconPack';
import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { FilterType, FilterValue } from '@/models';
import { FilterState } from '@/types/filter';
import CollapsiblePanel from '@/storybook/components/CollapsiblePanel/CollapsiblePanel';
import If from '@/storybook/components/If/If';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ReactElement, useMemo } from 'react';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './FiltersCollapse.module.css';

type SectionizedFilter = FilterType | FilterType[];

export type FiltersCollapseProps = {
  label?: string;
  badge?: ReactElement;
  defaultStateOpen?: boolean;
  disabled?: boolean;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  activeFiltersCount?: number;
  filterKeys?: SectionizedFilter[];
};

export enum FilterSectionDataTestId {
  ActiveFiltersCount,
  SectionLabel,
}

const ChevronIcon = ({ open }: { open: boolean }) => {
  return open === true ? <ChevronUpIcon /> : <ChevronDownIcon />;
};

const ValueLabel = ({ value }: { value: number }) => {
  if (value === 0) return null;

  return (
    <>
      <span>&nbsp;(</span>
      <span data-testid={FilterSectionDataTestId.ActiveFiltersCount}>{value}</span>
      <span>)</span>
    </>
  );
};

const FiltersCollapse = ({
  label = '',
  badge,
  defaultStateOpen,
  disabled,
  open,
  onToggle,
  children,
  activeFiltersCount = 0,
  filterKeys = [],
}: FiltersCollapseProps) => {
  const { t } = useTranslation();
  const { defaultFiltersState } = useFiltersContext();
  const { setValue, control } = useFormContext<FilterState>();

  // Safe cast: FilterType ⊂ Path<FilterState> (verified by setValue on line 80).
  // useWatch's array overload triggers TS2590 when checking FilterType[] against
  // Path<FilterState>[] due to ~6900 union members. Cannot be eliminated.
  const watchedFilters: FilterValue[] = useWatch({ control, name: filterKeys.flat() as Path<FilterState>[] });
  const hasChanges = useMemo(() => {
    return filterKeys.flat().some((key, index) => {
      const currentValue = watchedFilters[index];
      const defaultValue = defaultFiltersState?.[key];

      if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
        const currentValueString = currentValue.join(',');
        const defaultValueString = defaultValue.join(',') || '';
        return currentValueString !== defaultValueString;
      }

      return currentValue !== defaultValue;
    });
  }, [filterKeys, watchedFilters, defaultFiltersState]);

  const clearAllFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    filterKeys.flat().forEach(key => setValue(key, defaultFiltersState?.[key], { shouldDirty: true }));
  };

  return (
    <Disclosure defaultOpen={defaultStateOpen}>
      {() => (
        <>
          <DisclosureButton className={styles.disclosureButton} disabled={disabled} onClick={onToggle}>
            <div className={styles.filterSectionHeader}>
              <div className={styles.headerLabelContainer}>
                <div className={styles.headerLabel}>
                  <span data-testid={FilterSectionDataTestId.SectionLabel}>{label}</span>
                  <ValueLabel value={activeFiltersCount} />
                </div>
                {hasChanges && (
                  <div className={styles.clearAll} onClick={clearAllFilters} title="Clear filter">
                    {t('global.filters.clearAll')}
                  </div>
                )}
              </div>
              <If condition={badge !== undefined}>{badge}</If>
              <ChevronIcon open={open} />
            </div>
          </DisclosureButton>
          <CollapsiblePanel open={open} openedPanelHeight={'auto'}>
            <DisclosurePanel className={styles.disclosurePanel}>{children}</DisclosurePanel>
          </CollapsiblePanel>
        </>
      )}
    </Disclosure>
  );
};

export default FiltersCollapse;
